import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowRight, ArrowLeft, Puzzle, Check, Sun, Moon, Trash2, Plus, Webhook, Copy } from 'lucide-react';
import { apiClient, type CustomBlockInput, type CustomBlock, type CustomBlockParam } from '../../api/client';
import { IS_DEMO_MODE } from '@/lib/demoMode';

const DEMO_LIMIT_MESSAGE =
  'You\u2019ve reached the demo limit. Sign up for the free trial at platform.usekestrel.ai to keep building.';

const COLOR_PRESETS = [
  { value: '#7c6f9f', label: 'Muted Purple' },
  { value: '#5b7fa5', label: 'Muted Blue' },
  { value: '#4d9a9a', label: 'Muted Teal' },
  { value: '#5a9a6e', label: 'Muted Green' },
  { value: '#b89a5a', label: 'Muted Amber' },
  { value: '#b06060', label: 'Muted Red' },
  { value: '#9a6b8a', label: 'Muted Rose' },
  { value: '#6a6ea0', label: 'Muted Indigo' },
  { value: '#8a8078', label: 'Warm Gray' },
  { value: '#6e7a8a', label: 'Cool Gray' },
];

interface CreateCustomBlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** Demo builder: receives the locally-built block instead of persisting it. */
  onCreatedLocal?: (block: CustomBlock) => void;
  existingBlocks?: CustomBlock[];
  editingBlock?: CustomBlock | null;
}

export function CreateCustomBlockDialog({ open, onOpenChange, onCreated, onCreatedLocal, existingBlocks, editingBlock }: CreateCustomBlockDialogProps) {
  const [step, setStep] = useState<'curl' | 'form'>('curl');
  const [curlInput, setCurlInput] = useState('');
  const [webhookPayloadInput, setWebhookPayloadInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [iconUrlDark, setIconUrlDark] = useState('');
  const iconLightRef = React.useRef<HTMLInputElement>(null);
  const iconDarkRef = React.useRef<HTMLInputElement>(null);
  const MAX_ICON_SIZE = 128 * 1024;
  const [integrationName, setIntegrationName] = useState('');
  const [color, setColor] = useState('#7c6f9f');
  const [showIntegrationSuggestions, setShowIntegrationSuggestions] = useState(false);
  const [blockType, setBlockType] = useState<'action' | 'trigger'>('action');
  const [method, setMethod] = useState('POST');
  const [urlTemplate, setUrlTemplate] = useState('');
  const [headersText, setHeadersText] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [contentType, setContentType] = useState('application/json');
  const [authType, setAuthType] = useState('bearer');
  const [credential, setCredential] = useState('');
  const [responseMappingText, setResponseMappingText] = useState('');
  const [exampleResponse, setExampleResponse] = useState('');
  const [generatingMapping, setGeneratingMapping] = useState(false);
  const [parameters, setParameters] = useState<CustomBlockParam[]>([]);
  // Trigger-specific state
  const [eventTypePath, setEventTypePath] = useState('');
  const [payloadMappingText, setPayloadMappingText] = useState('');
  const [signatureHeader, setSignatureHeader] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [createdWebhookUrl, setCreatedWebhookUrl] = useState('');

  const isEditing = !!editingBlock;

  useEffect(() => {
    if (editingBlock) {
      setStep('form');
      setName(editingBlock.name);
      setDescription(editingBlock.description || '');
      setIconUrl(editingBlock.icon_url || '');
      setIconUrlDark(editingBlock.icon_url_dark || '');
      setIntegrationName(editingBlock.integration_name || '');
      setColor(editingBlock.color || '#7c6f9f');
      setMethod(editingBlock.method);
      setUrlTemplate(editingBlock.url_template);
      setContentType(editingBlock.content_type);
      setAuthType(editingBlock.auth_type);
      setBodyTemplate(editingBlock.body_template || '');
      setParameters(editingBlock.parameters || []);
      setCredential('');
      setBlockType((editingBlock.block_type as 'action' | 'trigger') || 'action');
      setEventTypePath(editingBlock.event_type_path || '');
      setSignatureHeader(editingBlock.signature_header || '');
      setWebhookSecret('');
      if (editingBlock.payload_mapping) {
        const lines = Object.entries(editingBlock.payload_mapping).map(([k, v]) => `${k}: ${v}`);
        setPayloadMappingText(lines.join('\n'));
      }
      if (editingBlock.webhook_path) {
        setCreatedWebhookUrl(`${window.location.origin}/api/webhooks/custom/${editingBlock.webhook_path}`);
      }
      if (editingBlock.headers_template) {
        const lines = Object.entries(editingBlock.headers_template).map(([k, v]) => `${k}: ${v}`);
        setHeadersText(lines.join('\n'));
      }
      if (editingBlock.response_mapping) {
        const lines = Object.entries(editingBlock.response_mapping).map(([k, v]) => `${k}: ${v}`);
        setResponseMappingText(lines.join('\n'));
      }
    }
  }, [editingBlock]);

  const existingIntegrations = useMemo(() => {
    if (!existingBlocks) return [];
    const map = new Map<string, { icon_url: string; icon_url_dark: string; color: string }>();
    for (const b of existingBlocks) {
      const iName = b.integration_name || '';
      if (!iName) continue;
      const existing = map.get(iName);
      if (!existing) {
        map.set(iName, { icon_url: b.icon_url || '', icon_url_dark: b.icon_url_dark || '', color: b.color || '' });
      } else {
        if (!existing.icon_url && b.icon_url) existing.icon_url = b.icon_url;
        if (!existing.icon_url_dark && b.icon_url_dark) existing.icon_url_dark = b.icon_url_dark;
        if (!existing.color && b.color) existing.color = b.color;
      }
    }
    return Array.from(map.entries()).map(([name, meta]) => ({ name, ...meta }));
  }, [existingBlocks]);

  const filteredSuggestions = useMemo(() => {
    if (!integrationName) return existingIntegrations;
    return existingIntegrations.filter(i =>
      i.name.toLowerCase().includes(integrationName.toLowerCase())
    );
  }, [integrationName, existingIntegrations]);

  const handleSelectIntegration = (iName: string) => {
    setIntegrationName(iName);
    setShowIntegrationSuggestions(false);
    const match = existingIntegrations.find(i => i.name === iName);
    if (match) {
      if (match.color) setColor(match.color);
      if (match.icon_url) setIconUrl(match.icon_url);
      if (match.icon_url_dark) setIconUrlDark(match.icon_url_dark);
    }
  };

  const handleIconFile = (e: React.ChangeEvent<HTMLInputElement>, setFn: (v: string) => void) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ICON_SIZE) {
      setError(`Icon must be under ${Math.round(MAX_ICON_SIZE / 1024)}KB. Selected: ${Math.round(file.size / 1024)}KB.`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('File must be an image (PNG, JPG, SVG, etc.)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFn(reader.result as string);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setStep('curl');
    setCurlInput('');
    setWebhookPayloadInput('');
    setName('');
    setDescription('');
    setIconUrl('');
    setIconUrlDark('');
    setIntegrationName('');
    setColor('#7c6f9f');
    setShowIntegrationSuggestions(false);
    setMethod('POST');
    setUrlTemplate('');
    setHeadersText('');
    setBodyTemplate('');
    setContentType('application/json');
    setAuthType('bearer');
    setCredential('');
    setResponseMappingText('');
    setParameters([]);
    setBlockType('action');
    setEventTypePath('');
    setPayloadMappingText('');
    setSignatureHeader('');
    setWebhookSecret('');
    setCreatedWebhookUrl('');
    setError('');
    setSaved(false);
  };

  const handleParseCurl = async () => {
    if (!curlInput.trim()) return;
    setParsing(true);
    setError('');
    try {
      const result = await apiClient.parseCurlToBlock(curlInput.trim(), exampleResponse.trim() || undefined);
      if (!result.success) {
        setError(result.error || 'Failed to parse cURL command');
        return;
      }
      setMethod(result.method || 'POST');
      setUrlTemplate(result.url_template || '');
      setContentType(result.content_type || 'application/json');
      setAuthType(result.auth_type || 'bearer');
      setName(result.suggested_name || '');
      setDescription(result.suggested_description || '');
      if (!integrationName && result.suggested_name) {
        setIntegrationName(result.suggested_name);
      }
      if (result.headers_template) {
        const lines = Object.entries(result.headers_template).map(([k, v]) => `${k}: ${v}`);
        setHeadersText(lines.join('\n'));
      }
      // Pretty-print body template if it's valid JSON
      let bodyTpl = result.body_template || '';
      if (bodyTpl) {
        try {
          const parsed = JSON.parse(bodyTpl);
          bodyTpl = JSON.stringify(parsed, null, 2);
        } catch { /* not JSON, keep as-is */ }
      }
      setBodyTemplate(bodyTpl);
      setParameters(result.parameters || []);
      if (result.response_mapping) {
        const lines = Object.entries(result.response_mapping).map(([k, v]) => `${k}: ${v}`);
        setResponseMappingText(lines.join('\n'));
      }
      setStep('form');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError(DEMO_LIMIT_MESSAGE);
      } else {
        setError(err?.message || 'Failed to parse cURL');
      }
    } finally {
      setParsing(false);
    }
  };

  const handleParseWebhook = async () => {
    if (!webhookPayloadInput.trim()) return;
    setParsing(true);
    setError('');
    try {
      const result = await apiClient.parseWebhookPayload(webhookPayloadInput.trim());
      if (!result.success) {
        setError(result.error || 'Failed to parse webhook payload');
        return;
      }
      setEventTypePath(result.event_type_path || '');
      setName(result.suggested_name || 'Custom Webhook');
      setDescription(result.suggested_description || '');
      if (!integrationName && result.suggested_name) {
        setIntegrationName(result.suggested_name.replace(' Webhook', ''));
      }
      if (result.payload_mapping) {
        const lines = Object.entries(result.payload_mapping).map(([k, v]) => `${k}: ${v}`);
        setPayloadMappingText(lines.join('\n'));
      }
      setStep('form');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError(DEMO_LIMIT_MESSAGE);
      } else {
        setError(err?.message || 'Failed to parse webhook payload');
      }
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (blockType === 'action' && !urlTemplate.trim()) {
      setError('URL is required for action blocks');
      return;
    }
    if (!integrationName.trim()) {
      setError('Integration name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headersMap: Record<string, string> = {};
      headersText.split('\n').forEach(line => {
        const idx = line.indexOf(':');
        if (idx > 0) headersMap[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      });
      const responseMapping: Record<string, string> = {};
      responseMappingText.split('\n').forEach(line => {
        const idx = line.indexOf(':');
        if (idx > 0) responseMapping[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      });
      const payloadMapping: Record<string, string> = {};
      payloadMappingText.split('\n').forEach(line => {
        const idx = line.indexOf(':');
        if (idx > 0) payloadMapping[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      });

      const input: CustomBlockInput = {
        name: name.trim(),
        description: description.trim(),
        icon_url: iconUrl.trim(),
        icon_url_dark: iconUrlDark.trim(),
        integration_name: integrationName.trim(),
        color,
        block_type: blockType,
        method: blockType === 'trigger' ? 'POST' : method,
        url_template: blockType === 'trigger' ? '' : urlTemplate.trim(),
        headers_template: blockType === 'trigger' ? {} : headersMap,
        body_template: blockType === 'trigger' ? '' : bodyTemplate,
        content_type: blockType === 'trigger' ? 'application/json' : contentType,
        auth_type: blockType === 'trigger' ? 'none' : authType,
        credential: blockType === 'trigger' ? undefined : credential,
        response_mapping: blockType === 'trigger' ? undefined : (Object.keys(responseMapping).length > 0 ? responseMapping : undefined),
        parameters: parameters.length > 0 ? parameters : undefined,
        event_type_path: blockType === 'trigger' ? eventTypePath.trim() : undefined,
        payload_mapping: blockType === 'trigger' ? payloadMapping : undefined,
        signature_header: blockType === 'trigger' ? signatureHeader.trim() : undefined,
        webhook_secret: blockType === 'trigger' ? webhookSecret : undefined,
      };

      let result;
      if (IS_DEMO_MODE) {
        // Public demo builder: blocks live only in this visitor's browser
        // session — persisting them to the shared demo tenant would leak one
        // visitor's API shapes/credentials to another.
        const now = new Date().toISOString();
        const localBlock: CustomBlock = {
          id: editingBlock?.id || `demo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
          tenant_id: 'demo',
          created_at: editingBlock?.created_at || now,
          updated_at: now,
          name: input.name,
          description: input.description,
          icon_url: input.icon_url,
          icon_url_dark: input.icon_url_dark,
          integration_name: input.integration_name,
          color: input.color,
          block_type: input.block_type,
          method: input.method,
          url_template: input.url_template,
          headers_template: input.headers_template || {},
          body_template: input.body_template,
          content_type: input.content_type,
          auth_type: input.auth_type,
          response_mapping: input.response_mapping,
          parameters: input.parameters,
          event_type_path: input.event_type_path,
          payload_mapping: input.payload_mapping,
          signature_header: input.signature_header,
          webhook_path:
            blockType === 'trigger'
              ? editingBlock?.webhook_path || `demo-${Math.random().toString(36).slice(2, 10)}`
              : undefined,
        };
        onCreatedLocal?.(localBlock);
        result = localBlock;
      } else if (isEditing && editingBlock) {
        result = await apiClient.updateCustomBlock(editingBlock.id, input);
      } else {
        result = await apiClient.createCustomBlock(input);
      }
      if (blockType === 'trigger' && result?.webhook_path) {
        setCreatedWebhookUrl(`${window.location.origin}/api/webhooks/custom/${result.webhook_path}`);
      }
      setSaved(true);
      onCreated();
      // Auto-close for action blocks; trigger blocks stay open so user can copy webhook URL
      if (blockType !== 'trigger') {
        setTimeout(() => { onOpenChange(false); reset(); }, 1500);
      }
    } catch (err: any) {
      setError(err?.response?.data || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const IconUpload = ({ label, icon, value, setFn, fileRef }: {
    label: string; icon: React.ReactNode; value: string;
    setFn: (v: string) => void; fileRef: React.RefObject<HTMLInputElement>;
  }) => (
    <div className="flex items-center gap-2">
      {icon}
      {value ? (
        <div className="relative">
          <img src={value} alt={label} className="w-7 h-7 rounded object-contain border" />
          <button onClick={() => setFn('')}
            className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-destructive text-destructive-foreground rounded-full text-[8px] flex items-center justify-center">x</button>
        </div>
      ) : (
        <div className="w-7 h-7 rounded border border-dashed flex items-center justify-center text-muted-foreground">
          <Puzzle className="w-3 h-3" />
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={e => handleIconFile(e, setFn)} className="hidden" />
      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => fileRef.current?.click()}>
        {value ? 'Change' : 'Upload'}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Custom Workflow Block' : 'Create Custom Workflow Block'}
          </DialogTitle>
          <DialogDescription>
            {blockType === 'trigger'
              ? 'Define a webhook-based trigger that starts workflows when external services send events.'
              : 'Define a custom HTTP action block that can be used in any workflow.'}
          </DialogDescription>
        </DialogHeader>

        {/* Block type toggle — only for create mode */}
        {!isEditing && step === 'curl' && (
          <div className="flex items-center gap-2 p-1 bg-muted rounded-lg w-fit">
            <button
              onClick={() => setBlockType('action')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${blockType === 'action' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Action Block
            </button>
            <button
              onClick={() => { setBlockType('trigger'); setStep('curl'); }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${blockType === 'trigger' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Trigger Block
            </button>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm rounded-md p-3">{error}</div>
        )}

        {step === 'curl' && !isEditing && (
          <div className="space-y-4">
            {blockType === 'action' ? (
            <>
            <div>
              <Label>Paste a cURL command</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Paste a cURL command and we'll automatically extract the method, URL, headers, and body. Credentials will be detected and stored securely.
              </p>
              <Textarea
                value={curlInput}
                onChange={e => setCurlInput(e.target.value)}
                placeholder={'curl -X POST https://api.example.com/v1/action \\\n  -H "Authorization: Bearer sk-..." \\\n  -H "Content-Type: application/json" \\\n  -d \'{"key": "value"}\''}
                className="font-mono text-xs min-h-[160px]"
              />
            </div>
            <div>
              <Label>Example API Response <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <p className="text-xs text-muted-foreground mb-2">
                Paste a sample JSON response to auto-generate response mapping fields.
              </p>
              <Textarea
                value={exampleResponse}
                onChange={e => setExampleResponse(e.target.value)}
                placeholder={'{\n  "data": { "id": "123", "status": "created" },\n  "message": "Success"\n}'}
                className="font-mono text-xs min-h-[80px]"
              />
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep('form')}>
                Skip — build from scratch
              </Button>
              <Button onClick={handleParseCurl} disabled={parsing || !curlInput.trim()} className="gap-2">
                {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Parse & Continue
              </Button>
            </div>
            </>
            ) : (
            <>
            <div>
              <Label>Paste an example webhook payload</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Paste a sample JSON payload from the external service and we'll automatically detect the event type field and extract useful fields for your workflow.
              </p>
              <Textarea
                value={webhookPayloadInput}
                onChange={e => setWebhookPayloadInput(e.target.value)}
                placeholder={'{\n  "type": "invoice.paid",\n  "data": {\n    "id": "inv_123",\n    "customer": "cus_456",\n    "amount": 9900,\n    "status": "paid"\n  }\n}'}
                className="font-mono text-xs min-h-[160px]"
              />
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep('form')}>
                Skip — build from scratch
              </Button>
              <Button onClick={handleParseWebhook} disabled={parsing || !webhookPayloadInput.trim()} className="gap-2">
                {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Parse & Continue
              </Button>
            </div>
            </>
            )}
          </div>
        )}

        {step === 'form' && !saved && (
          <div className="space-y-4">
            {/* Integration Name */}
            <div className="relative">
              <Label>Integration Name *</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Group blocks under the same integration. Select an existing one or type a new name.
              </p>
              <Input
                value={integrationName}
                onChange={e => { setIntegrationName(e.target.value); setShowIntegrationSuggestions(true); }}
                onFocus={() => setShowIntegrationSuggestions(true)}
                onBlur={() => setTimeout(() => setShowIntegrationSuggestions(false), 200)}
                placeholder="e.g. Twilio, SendGrid, Linear..."
              />
              {showIntegrationSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-40 overflow-y-auto">
                  {filteredSuggestions.map(i => (
                    <button key={i.name}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left"
                      onMouseDown={e => { e.preventDefault(); handleSelectIntegration(i.name); }}>
                      {i.icon_url ? <img src={i.icon_url} alt="" className="w-4 h-4 rounded object-contain" /> :
                        <span className="w-4 h-4 rounded" style={{ backgroundColor: i.color || '#7c6f9f' }} />}
                      <span>{i.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Color */}
            <div>
              <Label>Block Color</Label>
              <div className="flex items-center gap-1.5 mt-1">
                {COLOR_PRESETS.map(c => (
                  <button key={c.value} onClick={() => setColor(c.value)} title={c.label}
                    className={`w-5 h-5 rounded-full border-2 transition-all ${color === c.value ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c.value }} />
                ))}
                <Input type="color" value={color} onChange={e => setColor(e.target.value)}
                  className="w-7 h-7 p-0 border-0 cursor-pointer" />
              </div>
            </div>

            {/* Block Name + Icons */}
            <div>
              <Label>Block Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Send SMS" />
            </div>

            <div>
              <Label>Icons <span className="text-muted-foreground font-normal">(optional, max 128KB each)</span></Label>
              <div className="flex items-center gap-6 mt-1">
                <IconUpload label="Light mode" icon={<Sun className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  value={iconUrl} setFn={setIconUrl} fileRef={iconLightRef as React.RefObject<HTMLInputElement>} />
                <IconUpload label="Dark mode" icon={<Moon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  value={iconUrlDark} setFn={setIconUrlDark} fileRef={iconDarkRef as React.RefObject<HTMLInputElement>} />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this block do?" />
            </div>

            {/* Action block specific fields */}
            {blockType === 'action' && (
            <>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>URL *</Label>
                <Input value={urlTemplate} onChange={e => setUrlTemplate(e.target.value)} placeholder="https://api.example.com/v1/action" className="font-mono text-xs" />
              </div>
            </div>

            <div>
              <Label>Headers <span className="text-muted-foreground font-normal">(one per line: Key: Value)</span></Label>
              <Textarea value={headersText} onChange={e => setHeadersText(e.target.value)}
                placeholder={"Authorization: Bearer {{credential}}\nContent-Type: application/json"}
                className="font-mono text-xs min-h-[80px]" />
            </div>

            <div>
              <Label>Body Template</Label>
              <Textarea value={bodyTemplate} onChange={e => setBodyTemplate(e.target.value)}
                placeholder={'{"message": "{{step_outputs.action-1.summary}}"}'}
                className="font-mono text-xs min-h-[80px]" />
            </div>

            {/* Dynamic Parameters */}
            {parameters.length > 0 && (
              <div>
                <Label>Parameters</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  These fields will appear as configurable inputs when this block is used in a workflow. Values can reference previous steps (e.g. <code className="bg-muted px-1 rounded">{'{{request.message}}'}</code>) or be set from Slack requests.
                </p>
                <div className="space-y-2">
                  {parameters.map((p, i) => (
                    <div key={p.key} className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Input
                          value={p.key}
                          onChange={e => {
                            const updated = [...parameters];
                            updated[i] = { ...updated[i], key: e.target.value };
                            setParameters(updated);
                          }}
                          placeholder="key"
                          className="font-mono text-xs h-8"
                        />
                        <Input
                          value={p.label}
                          onChange={e => {
                            const updated = [...parameters];
                            updated[i] = { ...updated[i], label: e.target.value };
                            setParameters(updated);
                          }}
                          placeholder="Label"
                          className="text-xs h-8"
                        />
                        <Input
                          value={p.default_value || ''}
                          onChange={e => {
                            const updated = [...parameters];
                            updated[i] = { ...updated[i], default_value: e.target.value };
                            setParameters(updated);
                          }}
                          placeholder="Default value"
                          className="text-xs h-8"
                        />
                      </div>
                      <button onClick={() => setParameters(parameters.filter((_, j) => j !== i))}
                        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs"
              onClick={() => setParameters([...parameters, { key: '', label: '', default_value: '' }])}>
              <Plus className="w-3 h-3" /> Add Parameter
            </Button>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Auth Type</Label>
                <Select value={authType} onValueChange={setAuthType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                    <SelectItem value="api_key_header">API Key Header</SelectItem>
                    <SelectItem value="basic">Basic Auth</SelectItem>
                    <SelectItem value="custom">Custom (in headers)</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credential</Label>
                <Input type="password" value={credential} onChange={e => setCredential(e.target.value)}
                  placeholder={isEditing ? 'Leave blank to keep existing credential' : 'API key or token (stored securely)'} />
              </div>
            </div>

            <div>
              <Label>Response Mapping <span className="text-muted-foreground font-normal">(optional, one per line: output_key: $.json.path)</span></Label>
              <Textarea value={responseMappingText} onChange={e => setResponseMappingText(e.target.value)}
                placeholder={"summary: $.data.message\nstatus: $.status"} className="font-mono text-xs min-h-[60px]" />
            </div>
            </>
            )}

            {/* Trigger block specific fields */}
            {blockType === 'trigger' && (
            <>
            <div>
              <Label>Event Type Path</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Dot-notation path to extract the event type from the webhook JSON payload.
              </p>
              <Input value={eventTypePath} onChange={e => setEventTypePath(e.target.value)}
                placeholder="type  or  event.action  or  data.event_type"
                className="font-mono text-xs" />
            </div>

            <div>
              <Label>Payload Mapping <span className="text-muted-foreground font-normal">(one per line: signal_field: json.path)</span></Label>
              <p className="text-xs text-muted-foreground mb-1">
                Map webhook payload fields to signal metadata. These become available as {'{{signal.field_name}}'} in downstream blocks.
              </p>
              <Textarea value={payloadMappingText} onChange={e => setPayloadMappingText(e.target.value)}
                placeholder={"message: data.message\nresource_name: data.resource.name\nseverity: data.level"}
                className="font-mono text-xs min-h-[100px]" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Signature Header <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input value={signatureHeader} onChange={e => setSignatureHeader(e.target.value)}
                  placeholder="x-hub-signature-256  or  x-webhook-signature"
                  className="font-mono text-xs" />
              </div>
              <div>
                <Label>Webhook Secret <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input type="password" value={webhookSecret} onChange={e => setWebhookSecret(e.target.value)}
                  placeholder={isEditing ? 'Leave blank to keep existing' : 'Shared secret for HMAC verification'} />
              </div>
            </div>

            {createdWebhookUrl && (
              <div className="p-3 rounded-md border bg-muted/30">
                <Label className="text-xs font-medium">Webhook URL</Label>
                <p className="text-xs text-muted-foreground mb-1">Configure your external service to send webhooks to this URL:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-background p-2 rounded border truncate">{createdWebhookUrl}</code>
                  <Button type="button" variant="outline" size="sm" className="h-8 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(createdWebhookUrl); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
            </>
            )}

            <div className="flex items-center justify-between pt-2">
              {!isEditing && (
                <Button variant="ghost" size="sm" onClick={() => { setStep('curl'); if (blockType === 'trigger') setBlockType('action'); }} className="gap-1">
                  <ArrowLeft className="w-3 h-3" /> Back
                </Button>
              )}
              {isEditing && <div />}
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Puzzle className="w-4 h-4" />}
                {isEditing ? 'Save Changes' : 'Save Custom Block'}
              </Button>
            </div>
          </div>
        )}

        {saved && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <p className="font-medium">{isEditing ? 'Custom block updated' : 'Custom block created'}</p>
            <p className="text-sm text-muted-foreground">{isEditing ? `"${name}" has been updated.` : `You can now drag "${name}" from the sidebar into your workflow.`}</p>
            {blockType === 'trigger' && createdWebhookUrl && (
              <div className="mt-2 p-3 rounded-md border bg-muted/30 w-full max-w-md">
                <p className="text-xs font-medium mb-1">Webhook URL</p>
                <p className="text-xs text-muted-foreground mb-2">Configure your external service to send webhooks to this URL.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-background p-2 rounded border truncate">{createdWebhookUrl}</code>
                  <Button type="button" variant="outline" size="sm" className="h-7 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(createdWebhookUrl); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
            {blockType === 'trigger' && (
              <Button className="mt-4" onClick={() => { onOpenChange(false); reset(); }}>
                Done
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
